import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { decode } from 'he';
import {
  ServerMessage,
  ClientMessage,
  GameState,
  QuestionForClient,
  LeaderboardEntry,
  Player,
  StoredQuestion,
  OpenTriviaResponse,
  TOTAL_QUESTIONS,
  QUESTION_TIME_LIMIT_MS,
  MAX_POSSIBLE_SCORE,
  BASE_POINTS,
  MAX_SPEED_BONUS,
} from '@shared/protocol';
import { getWsUrl, getApiUrl } from '../config';

// Generate or retrieve player ID
function getPlayerId(sessionId: string): string {
  const key = `trivia_player_${sessionId}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

// Check if player has joined this specific session before
function hasJoinedSession(sessionId: string): boolean {
  return localStorage.getItem(`trivia_joined_${sessionId}`) === 'true';
}

// Mark that player has joined this session
function markJoinedSession(sessionId: string): void {
  localStorage.setItem(`trivia_joined_${sessionId}`, 'true');
}

// Get stored nickname
function getStoredNick(): string {
  return localStorage.getItem('trivia_nickname') || '';
}

// Save nickname
function saveNick(nick: string): void {
  localStorage.setItem('trivia_nickname', nick);
}

// Format time as mm:ss.mmm
function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
}

// Decode HTML entities
function decodeHtmlEntities(text: string): string {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.documentElement.textContent || text;
}

// Shuffle array
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Translate text using MyMemory API
async function translateToSpanish(text: string): Promise<string> {
  try {
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|es`
    );
    const data = await response.json() as { responseData?: { translatedText?: string } };
    return data.responseData?.translatedText || text;
  } catch {
    return text;
  }
}

// Translate a question and its answers
async function translateQuestion(question: StoredQuestion): Promise<StoredQuestion> {
  const [translatedQ, ...translatedAnswers] = await Promise.all([
    translateToSpanish(question.question),
    ...question.answers.map(a => translateToSpanish(a))
  ]);
  
  return {
    ...question,
    question: translatedQ,
    answers: translatedAnswers
  };
}

// Fetch questions from Open Trivia DB
async function fetchQuestions(): Promise<StoredQuestion[]> {
  const response = await fetch(`https://opentdb.com/api.php?amount=${TOTAL_QUESTIONS}&type=multiple`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch questions');
  }
  
  const data = await response.json() as OpenTriviaResponse;
  
  if (data.response_code !== 0 || !data.results?.length) {
    throw new Error('No questions returned');
  }
  
  return data.results.map(q => {
    const correctAnswer = decodeHtmlEntities(q.correct_answer);
    const allAnswers = [
      correctAnswer,
      ...q.incorrect_answers.map(decodeHtmlEntities),
    ];
    const shuffledAnswers = shuffleArray(allAnswers);
    const correctIndex = shuffledAnswers.indexOf(correctAnswer);
    
    return {
      question: decodeHtmlEntities(q.question),
      answers: shuffledAnswers,
      correctIndex,
    };
  });
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

function PlayerPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState(getStoredNick());
  const [hasJoined, setHasJoined] = useState(false);
  
  // Game state
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [currentQuestionData, setCurrentQuestionData] = useState<QuestionForClient | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  
  // Question state
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answerResult, setAnswerResult] = useState<{ correct: boolean; correctIndex: number; pointsEarned: number; answerTimeMs: number } | null>(null);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME_LIMIT_MS);
  const [questionStartTime, setQuestionStartTime] = useState<number | null>(null);
  const [waitingForOthers, setWaitingForOthers] = useState(false);
  
  // Continue functionality (host only)
  const [sessionLanguage, setSessionLanguage] = useState<'en' | 'es'>('en');
  const [isContinuing, setIsContinuing] = useState(false);
  const [continueStatus, setContinueStatus] = useState('');
  
  const wsRef = useRef<WebSocket | null>(null);
  const playerId = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRejoined = useRef(false);
  const handleMessageRef = useRef<(message: ServerMessage) => void>(() => {});

  const send = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const connect = useCallback(() => {
    if (!sessionId) return;
    
    playerId.current = getPlayerId(sessionId);
    
    const wsUrl = getWsUrl(`/api/session/${sessionId}/ws`);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      setConnectionStatus('connected');
      setError(null);
      
      // Only auto-rejoin if player previously joined THIS specific session
      // This prevents nickname from one session being used in another
      const storedNick = getStoredNick();
      if (storedNick && hasJoinedSession(sessionId) && !hasRejoined.current) {
        hasRejoined.current = true;
        send({
          type: 'hello',
          playerId: playerId.current,
          nick: storedNick,
        });
        setNickname(storedNick);
        setHasJoined(true);
      }
    };
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        handleMessageRef.current(message);
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };
    
    ws.onclose = () => {
      setConnectionStatus('disconnected');
      // Try to reconnect
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      reconnectRef.current = setTimeout(connect, 2000);
    };
    
    ws.onerror = () => {
      setConnectionStatus('error');
      setError('Connection error. Trying to reconnect...');
    };
  }, [sessionId, send]);

  const handleMessage = useCallback((message: ServerMessage) => {
    switch (message.type) {
      case 'state':
        setGameState(message.state);
        if (message.state.leaderboard) {
          setLeaderboard(message.state.leaderboard);
        }
        // Reset all game states when returning to lobby (after continue)
        if (message.state.status === 'lobby') {
          setCurrentQuestion(0);
          setCurrentQuestionData(null);
          setSelectedAnswer(null);
          setAnswerResult(null);
          setScore(0);
          setIsFinished(false);
          setWaitingForOthers(false);
          setLeaderboard([]);
          setQuestionStartTime(null);
          setTimeLeft(QUESTION_TIME_LIMIT_MS);
        }
        break;
        
      case 'host_assigned':
        setGameState(prev => prev ? { ...prev, hostPlayerId: message.hostPlayerId } : null);
        break;
        
      case 'player_update':
        setGameState(prev => prev ? { ...prev, players: message.players } : null);
        break;
        
      case 'game_started':
        // In sync mode, we only get the first question
        setCurrentQuestionData(message.questions[0]);
        setCurrentQuestion(0);
        setScore(0);
        setIsFinished(false);
        setSelectedAnswer(null);
        setAnswerResult(null);
        setWaitingForOthers(false);
        setQuestionStartTime(Date.now());
        setTimeLeft(QUESTION_TIME_LIMIT_MS);
        setGameState(prev => prev ? { ...prev, status: 'running', startedAt: message.startedAt } : null);
        break;
        
      case 'answer_result':
        setAnswerResult({ 
          correct: message.correct, 
          correctIndex: message.correctIndex,
          pointsEarned: message.pointsEarned,
          answerTimeMs: message.answerTimeMs,
        });
        setScore(message.totalScore);
        setWaitingForOthers(true);
        // Don't auto-advance - wait for next_question message from server
        break;
        
      case 'next_question':
        // Server says everyone has answered, move to next question
        setCurrentQuestion(message.questionIndex);
        setCurrentQuestionData(message.question);
        setSelectedAnswer(null);
        setAnswerResult(null);
        setWaitingForOthers(false);
        setQuestionStartTime(Date.now());
        setTimeLeft(QUESTION_TIME_LIMIT_MS);
        break;
        
      case 'game_finished':
        setLeaderboard(message.leaderboard);
        setIsFinished(true);
        setGameState(prev => prev ? { ...prev, status: 'finished' } : null);
        break;
        
      case 'error':
        setError(message.message);
        setTimeout(() => setError(null), 3000);
        break;
        
      case 'pong':
        // Keep-alive response
        break;
    }
  }, []);

  // Keep the ref updated with latest handler
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Ref to track if time-up auto-submit has been triggered for current question
  const timeUpTriggeredRef = useRef(false);

  // Reset timeUpTriggered when question changes
  useEffect(() => {
    timeUpTriggeredRef.current = false;
  }, [currentQuestion]);

  // Timer effect
  useEffect(() => {
    if (gameState?.status !== 'running' || isFinished || answerResult) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    
    timerRef.current = setInterval(() => {
      if (questionStartTime) {
        const elapsed = Date.now() - questionStartTime;
        const remaining = Math.max(0, QUESTION_TIME_LIMIT_MS - elapsed);
        setTimeLeft(remaining);
        
        if (remaining <= 0 && !timeUpTriggeredRef.current && selectedAnswer === null) {
          // Time's up - auto submit with wrong answer to move on
          timeUpTriggeredRef.current = true;
          // Clear the timer
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          // Send answer with -1 to indicate timeout (server will count as wrong)
          send({
            type: 'answer',
            questionIndex: currentQuestion,
            selectedIndex: -1,
          });
        }
      }
    }, 100);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameState?.status, isFinished, questionStartTime, answerResult, selectedAnswer, send, currentQuestion]);

  // Connect on mount
  useEffect(() => {
    connect();
    
    // Ping interval
    const pingInterval = setInterval(() => {
      send({ type: 'ping' });
    }, 30000);
    
    return () => {
      clearInterval(pingInterval);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect, send]);

  // Fetch session language for continue functionality
  useEffect(() => {
    if (!sessionId) return;
    fetch(getApiUrl(`/api/session/${sessionId}`))
      .then(res => res.json() as Promise<{ language?: 'en' | 'es' }>)
      .then(data => {
        if (data.language) setSessionLanguage(data.language);
      })
      .catch(() => {});
  }, [sessionId]);

  // Handle continue with new questions (host only)
  const handleContinue = async () => {
    if (!sessionId) return;
    
    setIsContinuing(true);
    setContinueStatus('Fetching new questions...');
    
    try {
      // Fetch new questions
      let questions = await fetchQuestions();
      
      // Translate if Spanish
      if (sessionLanguage === 'es') {
        setContinueStatus('Translating questions...');
        const translatedQuestions: StoredQuestion[] = [];
        for (const q of questions) {
          const translated = await translateQuestion(q);
          translatedQuestions.push(translated);
        }
        questions = translatedQuestions;
      }
      
      // Call continue endpoint
      setContinueStatus('Starting new round...');
      const response = await fetch(getApiUrl(`/api/session/${sessionId}/continue`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to continue session');
      }
      
      // Reset local state
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setAnswerResult(null);
      setIsFinished(false);
      setQuestionStartTime(null);
      setContinueStatus('');
    } catch (error) {
      console.error('Continue error:', error);
      setContinueStatus('Failed to continue. Try again.');
    } finally {
      setIsContinuing(false);
    }
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nickname.trim() || !sessionId) return;
    
    send({
      type: 'hello',
      playerId: playerId.current,
      nick: nickname.trim(),
    });
    setHasJoined(true);
    saveNick(nickname.trim());
    markJoinedSession(sessionId);
  };

  const handleStartGame = () => {
    send({ type: 'start_game' });
  };

  const handleAnswer = (index: number) => {
    if (selectedAnswer !== null || answerResult) return;
    
    setSelectedAnswer(index);
    send({
      type: 'answer',
      questionIndex: currentQuestion,
      selectedIndex: index,
    });
  };

  const isHost = gameState?.hostPlayerId === playerId.current;

  // Loading state
  if (connectionStatus === 'connecting') {
    return (
      <div className="loading-page">
        <div className="waiting-spinner" />
        <p>Connecting to game...</p>
      </div>
    );
  }

  // Nickname entry
  if (!hasJoined) {
    return (
      <div className="page page-centered">
        <div className="container container-narrow text-center">
          <div className="header">
            <div className="logo">👋</div>
            <h1>Join Game</h1>
            <div className="session-info">
              <span className="session-label">Code:</span>
              <strong>{sessionId}</strong>
            </div>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleJoin} className="card">
            <div className="form-group">
              <label className="form-label">Enter your nickname</label>
              <input
                type="text"
                className="form-input"
                placeholder="Your name"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={16}
                autoFocus
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary btn-large btn-full"
              disabled={!nickname.trim()}
            >
              🎮 Join Game
            </button>
          </form>
          
          <Link to="/" className="btn btn-secondary btn-full mt-2">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Results/Leaderboard
  if (gameState?.status === 'finished' && leaderboard.length > 0) {
    const myResult = leaderboard.find(e => e.playerId === playerId.current);
    const myRank = leaderboard.findIndex(e => e.playerId === playerId.current) + 1;
    const isWinner = myRank === 1;
    
    // Generate money bills for winner animation
    const moneyBills = isWinner ? Array.from({ length: 30 }, (_, i) => {
      const left = Math.random() * 100;
      const delay = Math.random() * 3;
      const duration = 3 + Math.random() * 4;
      const variant = i % 3;
      const emoji = ['💵', '💸', '🤑', '💰'][i % 4];
      return { id: i, left, delay, duration, variant, emoji };
    }) : [];
    
    return (
      <div className="page">
        {/* Flying money for winners */}
        {isWinner && (
          <div className="money-rain-container">
            {moneyBills.map(bill => (
              <span
                key={bill.id}
                className={`money-bill variant-${bill.variant}`}
                style={{
                  left: `${bill.left}%`,
                  animationDelay: `${bill.delay}s`,
                  animationDuration: `${bill.duration}s`,
                }}
              >
                {bill.emoji}
              </span>
            ))}
          </div>
        )}
        <div className="container">
          <div className="results-header">
            <div className="trophy-icon">{isWinner ? '👑' : '🏆'}</div>
            <h1>{isWinner ? 'YOU WON!' : 'Game Over!'}</h1>
          </div>
          
          {myResult && (
            <div className="your-result">
              <h3>Your Result</h3>
              <div className="result-big">{myResult.score} pts</div>
              <p className="mt-1">
                #{myRank} place • {Math.round((myResult.score / MAX_POSSIBLE_SCORE) * 100)}% of max score
              </p>
            </div>
          )}
          
          <h2>Leaderboard</h2>
          <ul className="leaderboard">
            {leaderboard.map((entry, index) => (
              <li 
                key={entry.playerId} 
                className={`leaderboard-item ${index === 0 ? 'winner' : ''} ${entry.playerId === playerId.current ? 'winner' : ''}`}
              >
                <div className="leaderboard-rank">
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                </div>
                <div className="leaderboard-info">
                  <div className="leaderboard-name">
                    {entry.nick}
                    {entry.playerId === playerId.current && ' (You)'}
                  </div>
                  <div className="leaderboard-stats">
                    <span>{entry.score} points</span>
                  </div>
                </div>
                <div className="leaderboard-time">
                  {formatTime(entry.totalTimeMs)}
                </div>
              </li>
            ))}
          </ul>
          
          {/* Host controls for continuing */}
          {isHost ? (
            <div className="host-continue-controls mt-3">
              {continueStatus && (
                <p className="text-muted text-center mb-2">{continueStatus}</p>
              )}
              <button
                className="btn btn-primary btn-full mb-2"
                onClick={handleContinue}
                disabled={isContinuing}
              >
                {isContinuing ? '⏳ Loading...' : '🔄 Continue with New Questions'}
              </button>
              <Link to="/" className="btn btn-secondary btn-full">
                🏠 New Game
              </Link>
            </div>
          ) : (
            <div className="mt-3 text-center">
              <p className="text-muted mb-2">Waiting for host to continue...</p>
              <Link to="/" className="btn btn-secondary btn-full">
                🏠 Leave Game
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Game in progress - playing
  if (gameState?.status === 'running' && currentQuestionData) {
    const question = currentQuestionData;
    const timerPercent = (timeLeft / QUESTION_TIME_LIMIT_MS) * 100;
    const timerSeconds = Math.ceil(timeLeft / 1000);
    
    // Waiting for others after answering
    if (waitingForOthers && answerResult) {
      return (
        <div className="page">
          <div className="container">
            <div className="game-header">
              <div className="question-progress">
                Question {currentQuestion + 1}/{TOTAL_QUESTIONS}
              </div>
              <div className="score-display">
                {score} pts
              </div>
            </div>
            
            <div className="question-card mb-3">
              <p className="question-text">{decode(question.question)}</p>
            </div>
            
            <div className="answer-feedback mb-3">
              {answerResult.correct ? (
                <div className="feedback-correct">
                  <div className="feedback-icon">✓</div>
                  <div className="feedback-text">
                    <strong>Correct!</strong>
                    <span>+{answerResult.pointsEarned} points</span>
                    <small>Answered in {(answerResult.answerTimeMs / 1000).toFixed(1)}s</small>
                  </div>
                </div>
              ) : (
                <div className="feedback-incorrect">
                  <div className="feedback-icon">✗</div>
                  <div className="feedback-text">
                    <strong>Wrong!</strong>
                    <span>Correct: {decode(question.answers[answerResult.correctIndex])}</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="waiting-for-others">
              <div className="waiting-spinner" />
              <p>Waiting for other players...</p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="page">
        <div className="container">
          <div className="game-header">
            <div className="question-progress">
              Question {currentQuestion + 1}/{TOTAL_QUESTIONS}
            </div>
            <div className={`timer ${timerSeconds <= 10 ? 'timer-warning' : ''} ${timerSeconds <= 5 ? 'timer-danger' : ''}`}>
              ⏱️ {timerSeconds}s
            </div>
          </div>
          
          <div className="timer-bar">
            <div 
              className="timer-bar-fill" 
              style={{ width: `${timerPercent}%` }}
            />
          </div>
          
          <div className="points-hint mb-2">
            <span>🎯 {BASE_POINTS} base + up to {MAX_SPEED_BONUS} speed bonus</span>
          </div>
          
          <div className="question-card">
            <p className="question-text">{decode(question.question)}</p>
          </div>
          
          <div className="answers-grid">
            {question.answers.map((answer, index) => {
              const labels = ['A', 'B', 'C', 'D'];
              let className = 'answer-btn';
              
              if (index === selectedAnswer) {
                className += ' selected';
              }
              
              return (
                <button
                  key={index}
                  className={className}
                  onClick={() => handleAnswer(index)}
                  disabled={selectedAnswer !== null}
                >
                  <span className="answer-label">{labels[index]}</span>
                  {decode(answer)}
                </button>
              );
            })}
          </div>
          
          <div className="mt-3 text-center">
            <span className="score-badge">Score: {score} pts</span>
          </div>
        </div>
      </div>
    );
  }

  // Lobby
  return (
    <div className="page">
      <div className="container container-narrow">
        <div className="text-center mb-3">
          <div className="logo" style={{ fontSize: '2rem' }}>🎮</div>
          <h1>Game Lobby</h1>
          <div className="session-info">
            <span className="session-label">Code:</span>
            <strong>{sessionId}</strong>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {connectionStatus === 'disconnected' && (
          <div className="error-message">
            Disconnected. Trying to reconnect...
          </div>
        )}
        
        <div className="card">
          <div className="card-header">
            <h3>Players ({gameState?.players.length || 0})</h3>
            {isHost && <span className="host-badge">You're the Host</span>}
          </div>
          
          <ul className="player-list">
            {gameState?.players.map((player: Player) => (
              <li key={player.playerId} className="player-item">
                <div className="player-avatar">
                  {player.nick.charAt(0).toUpperCase()}
                </div>
                <div className="player-info">
                  <div className="player-name">
                    {player.nick}
                    {player.playerId === playerId.current && ' (You)'}
                  </div>
                  <div className="player-status">
                    {player.playerId === gameState.hostPlayerId ? 'Host' : 'Player'}
                  </div>
                </div>
                <div className={player.connected ? 'connected-badge' : 'disconnected-badge'} />
              </li>
            ))}
            
            {(!gameState?.players || gameState.players.length === 0) && (
              <li className="player-item">
                <div className="waiting-spinner" style={{ width: '1.5rem', height: '1.5rem' }} />
                <div className="player-info">
                  <div className="player-name text-muted">Waiting for players...</div>
                </div>
              </li>
            )}
          </ul>
        </div>
        
        {isHost && (
          <button 
            className="btn btn-success btn-large btn-full mt-3"
            onClick={handleStartGame}
            disabled={(gameState?.players.length || 0) < 1}
          >
            🚀 Start Game
          </button>
        )}
        
        {!isHost && (
          <div className="waiting-container mt-3">
            <p className="text-muted">Waiting for host to start the game...</p>
          </div>
        )}
        
        <Link to="/" className="btn btn-secondary btn-full mt-2">
          ← Leave Game
        </Link>
      </div>
    </div>
  );
}

export default PlayerPage;
